import { PhoneType, type ScheduleAppointmentRequestV2 } from "@/lib/__generated/api";
import { DataLayerStepNames } from "@/lib/constants";
import { TransunionFraudCheckParams } from "@/lib/services/transunionFraudCheck";
import { getAppointmentDataLayerParams, getBaseDataLayerParams } from "@/lib/utils/dataLayerParams";
import { getSessionId } from "@/lib/utils/user-session-id";

import { schedulerSetup } from "../schedulerSetup";
import { isPatientUnder18 } from "../utils/patient-age.util";
import { isEligibilityBlocked } from "../utils/patientEligibility";
import {
  ScheduleAppointmentError,
  getScheduleAppointmentErrorCode,
} from "../utils/scheduleAppointment.util";
import { TransunionFraudCheckError } from "../utils/transunionFraudCheck.util";
import { ValidatePatientInput } from "../utils/validatePatient.util";

interface ScheduleAppointmentInput extends ScheduleAppointmentRequestV2 {
  isMinor: boolean;
  facilityCode?: string;
}

function getInsuranceFlag(dentalInsurance: string | undefined | null): boolean | undefined {
  return dentalInsurance ? dentalInsurance === "true" : undefined;
}

export const appointmentReviewState = schedulerSetup.createStateConfig({
  meta: { componentKey: "appointment-review", label: "reviewAndConfirm", showFooter: true },
  initial: "idle",
  entry: [
    {
      type: "dataLayerPush",
      params: ({ context }) => ({
        ...getAppointmentDataLayerParams(context),
        step_name: DataLayerStepNames.review,
        ...getBaseDataLayerParams(context),
      }),
    },
  ],
  // Leaving review to edit invalidates the verdict, so the next visit validates afresh and a
  // stale refusal can't keep the block (or its dialog) alive.
  exit: ["resetPatientValidationRequested", "clearValidatedPatientResponse"],
  states: {
    idle: {
      on: {
        SUBMIT_APPOINTMENT: [
          {
            guard: "isRecaptchaEnabled",
            target: "verifyingRecaptcha",
            actions: {
              type: "captureAppointmentReviewEvent",
              params: { message: "scheduler.confirm_appointment.clicked" },
            },
          },
          {
            target: "fraudCheck",
            actions: {
              type: "captureAppointmentReviewEvent",
              params: { message: "scheduler.confirm_appointment.clicked" },
            },
          },
        ],
        VALIDATE_PATIENT: {
          guard: "hasNotRequestedPatientValidation",
          target: "validatingPatient",
          actions: "markPatientValidationRequested",
        },
      },
    },
    // Validation refused this patient (eAPI's canSchedule verdict is authoritative). Only `idle`
    // accepts SUBMIT_APPOINTMENT, so a known-ineligible booking can't reach a downstream 400;
    // dismissing the dialog keeps the block, and the way out is BACK / GOTO_STEP to edit details.
    refused: {
      tags: ["patientRefused"],
      on: {
        SUBMIT_APPOINTMENT: {
          // Reaching here means the UI let a blocked submit through - worth knowing about,
          // since the button should have been disabled.
          actions: {
            type: "captureAppointmentReviewEvent",
            params: {
              message: "scheduler.confirm_appointment.blocked",
              errorCode: "PATIENT_INELIGIBLE",
            },
          },
        },
      },
    },
    verifyingRecaptcha: {
      tags: ["submitting"],
      invoke: {
        src: "verifyRecaptcha",
        input: ({ context }) => ({
          action: "submit",
          siteKey: context.recaptchaSiteKey,
        }),
        onDone: { target: "fraudCheck" },
        onError: {
          //For Production
          target: "idle",
          actions: [
            {
              type: "assignError",
              params: ({ event }) => ({
                key: "appointment",
                message: "Verification failed. Please try again.",
                errorCode: event.error instanceof Error ? event.error.message : "RECAPTCHA_FAILED",
              }),
            },
            {
              type: "captureAppointmentReviewEvent",
              params: ({ event }) => ({
                message: "scheduler.confirm_appointment.recaptcha_failed",
                errorCode: event.error instanceof Error ? event.error.message : "RECAPTCHA_FAILED",
              }),
            },
          ],
        },
      },
    },
    fraudCheck: {
      tags: ["submitting"],
      invoke: {
        src: "transunionFraudCheck",
        input: ({ context }): TransunionFraudCheckParams => ({
          emailAddress: context.patientData.emailAddress ?? "",
          ioBlackBox: (globalThis as any).IGLOO?.getBlackbox()?.blackbox ?? "",
          sku: context.reasonForVisit ?? "",
          pathUrl: globalThis.location?.pathname ?? "",
          tenantId: `${context.facility?.code}, ${context.facility?.name}`,
          phoneNumber: context.patientData.phoneNumber?.replaceAll(/[^\d]/g, "") ?? "",
          sessionId: getSessionId(),
        }),
        onDone: { target: "submitting" },
        onError: {
          target: "idle",
          actions: [
            {
              type: "assignError",
              params: ({ event }) => ({
                key: "appointment",
                message:
                  event.error instanceof TransunionFraudCheckError
                    ? event.error.message
                    : "Fraud check failed.",
                errorCode:
                  event.error instanceof TransunionFraudCheckError
                    ? event.error.errorCode
                    : "TU_FAIL",
              }),
            },
            {
              type: "captureAppointmentReviewEvent",
              params: ({ event }) => ({
                message: "scheduler.confirm_appointment.fraud_check_failed",
                errorCode:
                  event.error instanceof TransunionFraudCheckError
                    ? event.error.errorCode
                    : "TU_FAIL",
              }),
            },
          ],
        },
      },
    },
    submitting: {
      tags: ["submitting"],
      entry: {
        type: "captureAppointmentReviewEvent",
        params: { message: "scheduler.confirm_appointment.submission_started" },
      },
      invoke: {
        src: "scheduleAppointment",
        input: ({ context }): ScheduleAppointmentInput => ({
          patient: {
            firstName: context.patientData.firstName,
            lastName: context.patientData.lastName,
            email: context.patientData.emailAddress,
            dateOfBirth: context.patientData.dateOfBirth,
            phoneNumber: context.patientData.phoneNumber,
            phoneType: PhoneType.Mobile,
            // Links the booking to the patient's existing digital account. Without it eAPI
            // treats a known patient as new and rejects them (Schedule_NotNewPatient /
            // Schedule_PatientHasFutureAppointments) even after validate said they can schedule.
            digiact: context.validatedPatientResponse?.digiact ?? 0,
          },
          guardian: {
            firstName: context.guarantorData.firstName,
            lastName: context.guarantorData.lastName,
            email: context.guarantorData.email,
            dateOfBirth: context.guarantorData.dateOfBirth,
            phoneNumber: context.guarantorData.guarantorPhoneNumber,
            phoneType: PhoneType.Mobile,
          },
          questionnaireAnsweredQuestions: context.needBasedQuestionsAnswers,
          // Omitted when no questionnaire is loaded — eAPI treats a questionnaireId it did not
          // issue as a real reference, so a placeholder is not equivalent to leaving the key off.
          questionnaireId: context.questionnaire?.questionnaireBy?.questionnaireId ?? undefined,
          insuranceFlag: getInsuranceFlag(context.patientData.dentalInsurance),
          slotId: context.appointmentData.slotId,
          isMinor: isPatientUnder18(context),
          facilityCode: context.appointmentData.facilityCode ?? context.facility?.code,
        }),
        onDone: {
          target: "complete",
          actions: [
            {
              type: "assignScheduleAppointmentResponse",
              params: ({ event }) => ({ output: event.output }),
            },
            { type: "markStepComplete", params: { step: "appointmentReview" } },
            {
              type: "trackUserEvent",
              params: ({ context }) => ({
                eventType: "appointment_review",
                eventData: {
                  facility_code: context.facility?.code,
                  first_name: context.patientData.firstName,
                  last_name: context.patientData.lastName,
                  date_of_birth: context.patientData.dateOfBirth,
                  email_address: context.patientData.emailAddress,
                  mobile_phone: context.patientData.phoneNumber,
                  is_insured: getInsuranceFlag(context.patientData.dentalInsurance),
                  visit_reason: context.reasonForVisit,
                  appointment_start: context.appointmentData.rawTime,
                  appointment_end: context.appointmentData.rawTime,
                },
              }),
            },
            {
              type: "captureAppointmentReviewEvent",
              params: { message: "scheduler.confirm_appointment.succeeded" },
            },
          ],
        },
        onError: {
          target: "idle",
          actions: [
            {
              type: "assignError",
              params: ({ event }) => {
                const error = event.error;

                if (error instanceof ScheduleAppointmentError) {
                  return {
                    key: "appointment",
                    message: error.message,
                    errorCode: error.errorCode,
                  };
                }

                return {
                  key: "appointment",
                  message: "Failed to schedule appointment.",
                  errorCode: getScheduleAppointmentErrorCode(error),
                };
              },
            },
            {
              type: "captureAppointmentReviewEvent",
              params: ({ event }) => ({
                message: "scheduler.confirm_appointment.failed",
                errorCode: getScheduleAppointmentErrorCode(event.error),
              }),
            },
          ],
        },
      },
    },
    validatingPatient: {
      tags: ["validatingPatient"],
      invoke: {
        src: "validatePatient",
        input: ({ context }): ValidatePatientInput => ({
          firstName: context.patientData.firstName,
          lastName: context.patientData.lastName,
          dateOfBirth: context.patientData.dateOfBirth,
          phoneNumber: context.patientData.phoneNumber,
          facilityCode: context.appointmentData.facilityCode ?? context.facility?.code,
          digitalVisitReason: context.reasonForVisit ?? undefined,
        }),
        // The response is kept in both cases: the refusal dialog reads it, and a submit needs its
        // digiact.
        onDone: [
          {
            guard: ({ event }) => isEligibilityBlocked(event.output),
            target: "refused",
            actions: {
              type: "assignValidatedPatientResponse",
              params: ({ event }) => ({ output: event.output }),
            },
          },
          {
            target: "idle",
            actions: {
              type: "assignValidatedPatientResponse",
              params: ({ event }) => ({ output: event.output }),
            },
          },
        ],
        onError: {
          target: "idle", // fall back to idle so user isn't stuck
          actions: [
            {
              type: "assignError",
              params: () => ({
                key: "appointment",
                message: "Failed to validate patient.",
              }),
            },
            {
              type: "captureAppointmentReviewEvent",
              params: {
                message: "scheduler.confirm_appointment.validation_failed",
                errorCode: "VALIDATION_REQUEST_FAILED",
              },
            },
          ],
        },
      },
    },
    complete: {
      tags: ["submitting"],
      initial: "navigating",
      states: {
        navigating: {
          entry: {
            type: "pushConfirmationDataLayer", // fires GTM event with eventCallback when external nav is on
          },
          // In-app path (flag ON): no navigation to gate on, bail out immediately after
          // pushing GTM. Only the external-nav path (flag off/absent) needs to wait for
          // `NAVIGATION_READY` (or the 1000ms fallback) so GTM can flush before
          // `performNavigation` unloads the page.
          always: {
            guard: ({ context }) => context.disableExternalConfirmation,
            target: "done",
          },
          // XState-managed fallback timer — auto-cancelled on state exit
          after: {
            1000: { target: "done" },
          },
          on: {
            // GTM callback fires this when all tags complete
            NAVIGATION_READY: { target: "done" },
          },
        },
        done: { type: "final" },
      },
      onDone: { target: "finished" },
    },
    finished: { type: "final" },
  },
  on: {
    BACK: [
      { guard: "isPatientUnder18", target: "guarantorInformation" },
      { guard: "isSlotFirstFlow", target: "patientInformation" },
      { target: "dateTimeSelection" },
    ],
  },
  // Both flows reach this via `complete → finished`. The external-nav actions only run
  // when the feature flag is on; the in-app flow just transitions to the terminal
  // `confirmation` state without touching the cookie or redirecting.
  onDone: [
    {
      guard: "isExternalConfirmationEnabled",
      target: "confirmation",
      actions: ["writeAppointmentCookie", "performNavigation"],
    },
    { target: "confirmation" },
  ],
} as const);
